import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  type JobStore,
  type FinalReturnYearInput,
} from '@/features/jobs/JobStore'
import { type Job, type JobReturnAmountsYear } from '@/features/jobs/Job'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { StateAmountEntryViewModel } from '@/features/jobs/details/components/FinalReturnAmountsDialog/StateAmountEntryViewModel'
import { YearAmountEntryViewModel } from '@/features/jobs/details/components/FinalReturnAmountsDialog/YearAmountEntryViewModel'

/**
 * Converts the job's recorded amounts into dialog inputs. The server's own
 * tax years are round-tripped rather than re-derived, which keeps a
 * Manual save aligned with the Extracted year it overrides.
 */
export function returnAmountsToYearInputs(
  amounts: JobReturnAmountsYear[],
): FinalReturnYearInput[] {
  return amounts.map((year) => ({
    taxYear: year.taxYear,
    // `federalAmount` is absent when the year carries state amounts but no
    // federal amount. Round-trip that absence rather than defaulting it to
    // zero, or an untouched save would write a bogus $0 federal amount.
    federalAmount: year.federalAmount,
    stateAmounts: year.stateAmounts.map((s) => ({
      state: s.state,
      amount: s.amount,
    })),
  }))
}

/**
 * Dialog for entering per-year final return amounts when a Pro marks a
 * RETAIL return as filed after uploading a Final Return document.
 *
 * Amounts are signed: positive = refund to client, negative = owed.
 */
export class FinalReturnAmountsDialogViewModel {
  // Resets so a cancelled edit doesn't linger.
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * One entry per tax year being edited.
   */
  @observable
  accessor yearEntries: YearAmountEntryViewModel[] = []

  constructor(
    private readonly _jobStore: JobStore,
    private readonly _job: Job,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _onComplete: () => Promise<void>,
    private readonly _initialYears: () => FinalReturnYearInput[],
  ) {
    this.reset()
  }

  /**
   * Whether an entry still misses its tax year or a state amount.
   */
  @computed
  get hasIncompleteEntries() {
    return this.yearEntries.some((y) => !y.isCompleted)
  }

  /**
   * Selectable tax years for the dropdown: the current calendar year down
   * through the previous ten (late filings and amendments reach back years).
   */
  @computed
  get taxYearOptions(): [string, string][] {
    const current = new Date().getFullYear()
    return Array.from({ length: 11 }, (_, i) => {
      const year = String(current - i)
      return [year, year]
    })
  }

  /**
   * Shows the dialog, starting from the job's current amounts rather than the
   * snapshot taken at construction, since its owners are built well before
   * it is ever shown.
   */
  @action.bound
  show() {
    this.reset()
    this.modal.show()
  }

  /**
   * Adds an empty tax year entry.
   */
  @action.bound
  addYearEntry() {
    this.yearEntries.push(new YearAmountEntryViewModel())
  }

  /**
   * Removes the tax year entry at the index.
   */
  @action.bound
  removeYearEntry(index: number) {
    this.yearEntries.splice(index, 1)
  }

  /**
   * Rebuilds the entries from the job's recorded amounts so the next open
   * starts from the current server state, discarding any uncommitted edits.
   */
  @action.bound
  reset() {
    const initial = this._initialYears()

    this.yearEntries = initial.length
      ? initial.map(
          (year) =>
            new YearAmountEntryViewModel(
              year.taxYear,
              year.federalAmount,
              year.stateAmounts.map(
                (s) => new StateAmountEntryViewModel(s.state, s.amount),
              ),
            ),
        )
      : [new YearAmountEntryViewModel()]
  }

  /**
   * Records the amounts for the job.
   */
  submit = task.resolved(async () => {
    if (this.hasIncompleteEntries) {
      return
    }

    const years: FinalReturnYearInput[] = this.yearEntries.map((y) => ({
      taxYear: y.taxYear,
      federalAmount: y.hasFederalAmount ? y.federalAmount : undefined,
      stateAmounts: y.stateEntries.map((s) => ({
        state: s.state,
        amount: s.amount,
      })),
    }))

    await this._jobStore
      .recordJobReturnAmounts(this._job.id, years)
      .catch(
        this._createAlertErrorHandler('Recording the return amounts failed'),
      )
    this.modal.close()
    await this._onComplete()
  })
}
